// Slide-out nav drawer — opens from the header hamburger. Fixed layout:
// HOME, SETTINGS, the five ORDER dropdowns (Objectives → Revise, filled from
// the user's Sidebar Configuration), then an ALL TOOLS dropdown that lists
// every tool inline (no separate page). SyQi AI, SHAPE and Seek are gone.
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Image, Modal, Platform, Pressable, ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { ChevronDown, ChevronRight } from 'lucide-react-native';
import { useTheme, makeThemedStyles, DISPLAY_FONT, radii } from '../v10/theme';
import { useSidebarConfig, resolveSidebarDest, type SidebarItem } from './useSidebarConfig';
import { iconForDest, iconForTool } from './toolIcons';
import { ALL_TOOL_IDS, TOOL_NAMES } from '../domain/olmps';
import { SIDEBAR_GEAR_GROUPS } from './sidebarGearGroups';
import { SEAT_BY_ID, DEITY_WORD, portraitFor } from '../v10/roster';
import { GlassCard } from '../v10/ui';
import { GlassFill } from './glass';

const DRAWER_WIDTH_RATIO = 0.78;

export function NavDrawer({
  open,
  userId,
  onClose,
  onOpenTool,
  onOpenHome,
  onOpenSettings,
  onClosed,
}: {
  open: boolean;
  userId: string | undefined;
  onClose: () => void;
  onOpenTool: (toolId: string) => void;
  onOpenHome: () => void;
  onOpenSettings: () => void;
  /** Fired once the drawer Modal has fully unmounted after closing. */
  onClosed?: () => void;
}) {
  const { width } = useWindowDimensions();
  const { t } = useTheme();
  const styles = useStyles();
  const drawerWidth = Math.min(320, width * DRAWER_WIDTH_RATIO);
  const translateX = useRef(new Animated.Value(-drawerWidth)).current;
  const backdropOpacity = useRef(new Animated.Value(0)).current;
  // Keep the Modal mounted through the close animation so the panel slides
  // out (instead of popping) before it unmounts.
  const [rendered, setRendered] = useState(false);
  // Dropdown sections default closed; multiple can be open at once.
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const { visibleItems } = useSidebarConfig(userId);

  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;
  const wasRendered = useRef(false);
  useEffect(() => {
    // Fire onClosed after the Modal is truly unmounted (rendered true -> false).
    if (rendered) wasRendered.current = true;
    else if (wasRendered.current) {
      wasRendered.current = false;
      onClosedRef.current?.();
    }
  }, [rendered]);

  useEffect(() => {
    const useNativeDriver = Platform.OS !== 'web';
    let fallback: ReturnType<typeof setTimeout> | undefined;
    if (open) setRendered(true);
    Animated.parallel([
      Animated.timing(translateX, { toValue: open ? 0 : -drawerWidth, duration: 260, useNativeDriver }),
      Animated.timing(backdropOpacity, { toValue: open ? 1 : 0, duration: 260, useNativeDriver }),
    ]).start(({ finished }) => {
      if (finished && !open) setRendered(false);
    });
    // Guarantee unmount even if the animation callback never fires.
    if (!open) fallback = setTimeout(() => setRendered(false), 400);
    return () => {
      if (fallback) clearTimeout(fallback);
    };
  }, [open, drawerWidth, translateX, backdropOpacity]);

  const handleSelect = (item: (typeof visibleItems)[number]) => {
    const dest = resolveSidebarDest(item);
    if (!dest) return;
    onClose();
    if (dest.kind === 'home') onOpenHome();
    else if (dest.kind === 'settings') onOpenSettings();
    else onOpenTool(dest.toolId);
  };

  // Section item lookup is by deity toolId, not the user-editable Settings →
  // Sidebar groups (those stay a separate freeform feature) — the dropdown
  // sections are the fixed ORDER-gear roster from sidebarGearGroups.
  const itemByToolId = new Map<string, SidebarItem>();
  for (const item of visibleItems) {
    const dest = resolveSidebarDest(item);
    if (dest?.kind === 'tool') itemByToolId.set(dest.toolId, item);
  }
  // ORACLE is renamed ATLAS and TELOS folds into it — their saved sidebar
  // items are absorbed (not shown); Objectives always renders, led by ATLAS.
  const RETIRED_ORIENT = ['oracle', 'telos'];
  const sections = SIDEBAR_GEAR_GROUPS.map((group) => ({
    group,
    items: group.toolIds
      .filter((id) => !RETIRED_ORIENT.includes(id))
      .map((id) => itemByToolId.get(id))
      .filter((i): i is SidebarItem => !!i),
  }))
    // SHAPE is not part of ORDER; it is reachable through ALL TOOLS.
    .filter((s) => s.group.key !== 'shape')
    .filter((s) => s.items.length > 0 || s.group.key === 'objectives');
  const openTool = (id: string) => {
    onClose();
    onOpenTool(id);
  };
  const HomeIcon = iconForDest({ kind: 'home' });
  const SettingsIcon = iconForDest({ kind: 'settings' });
  const allToolsOpen = openGroups.has('all-tools');
  const AllToolsChevron = allToolsOpen ? ChevronDown : ChevronRight;

  return (
    <Modal visible={rendered} transparent animationType="none" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <Animated.View pointerEvents={open ? 'auto' : 'none'} style={[StyleSheet.absoluteFill, styles.scrim, { opacity: backdropOpacity }]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        </Animated.View>
        <Animated.View style={[styles.drawer, { width: drawerWidth, transform: [{ translateX }] }]}>
          <GlassFill strong radius={radii.panel} />
          <ScrollView contentContainerStyle={styles.content}>
            <Text style={styles.title}>Menu</Text>

            <Pressable
              style={styles.row}
              onPress={() => {
                onClose();
                onOpenHome();
              }}
            >
              <HomeIcon size={20} color={t.ivorySoft} strokeWidth={2} />
              <Text style={styles.rowText}>Home</Text>
            </Pressable>
            <Pressable
              style={styles.row}
              onPress={() => {
                onClose();
                onOpenSettings();
              }}
            >
              <SettingsIcon size={20} color={t.ivorySoft} strokeWidth={2} />
              <Text style={styles.rowText}>Settings</Text>
            </Pressable>

            {sections.map(({ group, items }) => {
              const open = openGroups.has(group.key);
              const Chevron = open ? ChevronDown : ChevronRight;
              return (
                <View key={group.key} style={styles.group}>
                  <Pressable style={styles.groupHeader} onPress={() => toggleGroup(group.key)}>
                    <Text style={styles.groupTitle}>{group.label}</Text>
                    <Chevron size={16} color={t.ivorySoft} strokeWidth={2.5} />
                  </Pressable>
                  {open && (
                    <>
                      {/* POROS — Resource Readiness, first in REGULATE. */}
                      {group.key === 'regulate' && !itemByToolId.has('poros') && (
                        <DeityRow toolId="poros" onPress={() => openTool('poros')} />
                      )}
                      {/* ATLAS — the unified ORIENT tool, always first in its section. */}
                      {group.key === 'objectives' && !itemByToolId.has('atlas') && (
                        <DeityRow toolId="atlas" onPress={() => openTool('atlas')} />
                      )}
                      {items.map((item) => (
                        <DrawerRow key={item.id} item={item} onPress={() => handleSelect(item)} />
                      ))}
                    </>
                  )}
                </View>
              );
            })}

            <View style={styles.group}>
              <Pressable style={styles.groupHeader} onPress={() => toggleGroup('all-tools')}>
                <Text style={[styles.groupTitle, styles.allToolsText]}>All tools</Text>
                <AllToolsChevron size={16} color={t.goldBright} strokeWidth={2.5} />
              </Pressable>
              {/* ORACLE is renamed ATLAS; TELOS folds into it. */}
              {allToolsOpen &&
                ALL_TOOL_IDS.filter((id) => !['oracle', 'telos'].includes(id)).map((id) => (
                  <DeityRow key={id} toolId={id} onPress={() => openTool(id)} />
                ))}
            </View>
          </ScrollView>
        </Animated.View>
      </View>
    </Modal>
  );
}

function DrawerRow({ item, onPress }: { item: SidebarItem; onPress: () => void }) {
  const { t } = useTheme();
  const styles = useStyles();
  const dest = resolveSidebarDest(item);
  if (dest?.kind === 'tool') return <DeityRow toolId={dest.toolId} label={item.label} onPress={onPress} />;
  const Icon = iconForDest(dest);
  return (
    <Pressable style={styles.row} onPress={onPress}>
      <Icon size={20} color={t.ivorySoft} strokeWidth={2} />
      <Text style={styles.rowText}>{item.label}</Text>
    </Pressable>
  );
}

/** Same look as Settings → Deities: portrait, NAME — Word. Tools without a
 *  portrait (POROS, EPIC, SHAPE deities…) show their icon in the circle. */
function DeityRow({ toolId, label, onPress }: { toolId: string; label?: string; onPress: () => void }) {
  const { t } = useTheme();
  const styles = useStyles();
  // The ATLAS tool opens as 'atlas' but sits in the 'telos' seat.
  const seatId = toolId === 'atlas' ? 'telos' : toolId;
  const portrait = portraitFor(seatId);
  const name = SEAT_BY_ID[seatId]?.name ?? TOOL_NAMES[toolId] ?? label ?? toolId.toUpperCase();
  const word = DEITY_WORD[toolId];
  return (
    <Pressable onPress={onPress}>
      <GlassCard style={styles.deityRow}>
        {portrait ? (
          <Image source={portrait} style={styles.deityThumb} />
        ) : (
          <View style={[styles.deityThumb, styles.deityThumbIcon]}>
            {React.createElement(iconForTool(toolId), { size: 18, color: t.ivorySoft, strokeWidth: 2 })}
          </View>
        )}
        <Text style={styles.deityName}>{name}</Text>
        {word ? (
          <Text style={styles.deityWord} numberOfLines={1}>
            — {word}
          </Text>
        ) : null}
      </GlassCard>
    </Pressable>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
    backdrop: { flex: 1, flexDirection: 'row', justifyContent: 'flex-start' },
    scrim: { backgroundColor: c.overlay },
    drawer: {
      height: '100%',
      backgroundColor: 'transparent',
      overflow: 'hidden',
      borderTopRightRadius: radii.panel,
      borderBottomRightRadius: radii.panel,
      borderRightWidth: 1,
      borderColor: t.navyLine,
      paddingTop: 60,
      ...neu.raisedLg,
    },
    content: { paddingHorizontal: 18, paddingBottom: 40 },
    title: { fontSize: 13, fontWeight: '800', letterSpacing: 2.5, color: t.ivoryFaint, marginBottom: 12, textTransform: 'uppercase', fontFamily: DISPLAY_FONT },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 14,
      paddingVertical: 13,
    },
    rowText: { fontSize: 16, fontWeight: '700', color: t.ivory, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
    deityRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      borderRadius: radii.tile,
      paddingVertical: 8,
      paddingHorizontal: 10,
      marginTop: 8,
    },
    deityThumb: { width: 34, height: 34, borderRadius: 17 },
    deityThumbIcon: { alignItems: 'center', justifyContent: 'center', backgroundColor: t.lineFaint },
    deityName: { fontSize: 15, fontWeight: '800', color: t.ivory, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
    deityWord: { flexShrink: 1, fontSize: 14, fontWeight: '700', color: t.goldBright },
    group: { marginTop: 16 },
    groupHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 6,
      borderBottomWidth: 1,
      borderBottomColor: t.lineFaint,
    },
    groupTitle: { fontSize: 13, fontWeight: '800', color: t.ivorySoft, textTransform: 'uppercase', letterSpacing: 2, fontFamily: DISPLAY_FONT },
    allToolsText: { color: t.goldBright },
  }),
);
